import React, { useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { IcoLn } from '../ActionIcons';
import useDrawerA11y from './useDrawerA11y';
import DrawerFooter from './DrawerFooter';

// The ONE centered dialog (audit 46858 step 7). Same Esc / focus trap / focus restore as the drawers (useDrawerA11y, topmost wins,
// so a modal over a drawer takes the key first). Always portaled to body on the shell z-scale (shell.css --z-modal).
// size sm 420 | md 560 | lg 760. Header = title + close ✕ (aria EN+FR). Footer = DrawerFooter: Close/Cancel left as a bordered .btn
// (closeLabel), `secondary`, main action right-most (`primary`: btn success, or btn danger for destructive). footer={false} = no footer.
// Body scrolls inside the dialog. Scrim click closes unless dismissOnScrim={false} (use it for forms with unsaved input).
export default function ShellModal({ title, onClose, size = 'md', open = true, role = 'dialog', primary, secondary, start, closeLabel, closeDisabled, closeTestId = "sm-cancel", footer = true, dismissOnScrim = true, testId, className = '', bodyClassName = '', children }) {
  const { t } = useTranslation('actions');
  const ref = useRef(null);
  const titleId = useId();
  useDrawerA11y(ref, onClose, open);
  if (!open) return null;
  return createPortal(
    <div className="sm-wrap" onMouseDown={e => { if (dismissOnScrim && e.target === e.currentTarget && onClose) onClose(); }}>
      <div ref={ref} tabIndex={-1} role={role} aria-modal="true" aria-labelledby={titleId} data-testid={testId} className={`sm-modal sm-${size} ${className}`.trim()}>
        <div className="sm-head">
          <div className="sm-title" id={titleId}>{title}</div>
          <button type="button" className="tool" title={t('close')} aria-label={t('close')} onClick={onClose} data-testid="sm-close"><IcoLn name="close" /></button>
        </div>
        <div className={`sm-body ${bodyClassName}`.trim()}>{children}</div>
        {footer && <DrawerFooter onClose={onClose} closeLabel={closeLabel} closeDisabled={closeDisabled} start={start} secondary={secondary} primary={primary} closeTestId={closeTestId} />}
      </div>
    </div>,
    document.body,
  );
}

// Title + message + Cancel left / confirm right. danger = destructive (outline red), else btn success.
export function ConfirmModal({ title, message, confirmLabel, cancelLabel, danger = false, busy = false, onConfirm, onCancel, role = 'alertdialog', testId, children }) {
  const { t } = useTranslation('actions');
  return (
    <ShellModal title={title} size="sm" role={role} onClose={onCancel} closeLabel={cancelLabel || t('cancel')} closeDisabled={busy} testId={testId}
      primary={<button type="button" className={danger ? 'btn danger' : 'btn success'} disabled={busy} onClick={onConfirm} data-testid="sm-confirm">{confirmLabel || t('confirm')}</button>}>
      {message && <p className="sm-msg">{message}</p>}
      {children}
    </ShellModal>
  );
}
